import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Store, Mail, Lock, User, Phone, Building, FileText, ArrowRight, Check, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axiosInstance from '../api/axios';

const VendorRegister = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    // Personal Info
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    password_confirm: '',
    phone: '',
    // Business Info
    shop_name: '',
    shop_description: '',
    tax_id: '',
    business_address: ''
  });

  const [errors, setErrors] = useState({});

  const validateStep1 = () => {
    const newErrors = {};
    
    if (!formData.first_name.trim()) newErrors.first_name = 'Vorname ist erforderlich';
    if (!formData.last_name.trim()) newErrors.last_name = 'Nachname ist erforderlich';
    if (!formData.email.trim()) newErrors.email = 'E-Mail ist erforderlich';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Ungültige E-Mail-Adresse';
    if (!formData.password) newErrors.password = 'Passwort ist erforderlich';
    else if (formData.password.length < 6) newErrors.password = 'Passwort muss mindestens 6 Zeichen haben';
    if (formData.password !== formData.password_confirm) newErrors.password_confirm = 'Passwörter stimmen nicht überein';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    
    if (!formData.shop_name.trim()) newErrors.shop_name = 'Shop-Name ist erforderlich';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) return;
    
    setLoading(true);
    
    try {
      await axiosInstance.post('/vendors/register', {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        shop_name: formData.shop_name,
        shop_description: formData.shop_description,
        tax_id: formData.tax_id,
        business_address: formData.business_address
      });
      
      setSuccess(true);
      toast.success('Registrierung erfolgreich eingereicht!');
      
    } catch (error) {
      const message = error.response?.data?.detail || 'Registrierung fehlgeschlagen';
      toast.error(message);
      setErrors({ submit: message });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  // Benefits for vendors
  const benefits = [
    { icon: Store, title: 'Eigener Shop', desc: 'Präsentieren Sie Ihre Produkte auf unserer Plattform' },
    { icon: Check, title: 'Einfacher Start', desc: 'Keine monatlichen Grundgebühren im Basis-Tarif' },
    { icon: Building, title: 'Professionell', desc: 'Nutzen Sie unsere bestehende Infrastruktur' },
  ];

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: '#D4C4B0' }}>
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full mx-auto mb-4 flex items-center justify-center">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Registrierung eingereicht!</h2>
            <p className="text-gray-600 mb-6">
              Vielen Dank für Ihre Anmeldung als Verkäufer bei Homeyoursoul. 
              Wir prüfen Ihre Anfrage und melden uns in Kürze bei Ihnen.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Sie erhalten eine E-Mail an <strong>{formData.email}</strong>, 
              sobald Ihr Account freigeschaltet wurde.
            </p>
            <Link to="/">
              <Button className="w-full">
                Zurück zur Startseite
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 pb-24 lg:pb-8" style={{ backgroundColor: '#D4C4B0' }}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <h1 className="text-3xl font-bold text-gray-900">Homeyoursoul</h1>
          </Link>
          <h2 className="text-2xl font-semibold text-gray-800">Als Verkäufer registrieren</h2>
          <p className="text-gray-600 mt-2">Werden Sie Teil unseres Marktplatzes</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Benefits Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Ihre Vorteile</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <benefit.icon className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{benefit.title}</p>
                      <p className="text-xs text-gray-500">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            <Card className="bg-purple-50 border-purple-200">
              <CardContent className="p-4">
                <h4 className="font-semibold text-purple-900 mb-2">Basis-Tarif</h4>
                <ul className="text-sm text-purple-800 space-y-1">
                  <li>• 8% Provision pro Verkauf</li>
                  <li>• Keine monatlichen Kosten</li>
                  <li>• Bis zu 50 Produkte</li>
                  <li>• Monatliche Auszahlung</li>
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Registration Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>
                    {step === 1 ? 'Persönliche Daten' : 'Shop-Informationen'}
                  </CardTitle>
                  <div className="flex gap-2">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>1</div>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step >= 2 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>2</div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit}>
                  {step === 1 && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Vorname *</Label>
                          <div className="relative mt-1">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              value={formData.first_name}
                              onChange={(e) => handleChange('first_name', e.target.value)}
                              className={`pl-10 ${errors.first_name ? 'border-red-500' : ''}`}
                              placeholder="Max"
                            />
                          </div>
                          {errors.first_name && <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>}
                        </div>
                        <div>
                          <Label>Nachname *</Label>
                          <div className="relative mt-1">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              value={formData.last_name}
                              onChange={(e) => handleChange('last_name', e.target.value)}
                              className={`pl-10 ${errors.last_name ? 'border-red-500' : ''}`}
                              placeholder="Mustermann"
                            />
                          </div>
                          {errors.last_name && <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>}
                        </div>
                      </div>

                      <div>
                        <Label>E-Mail-Adresse *</Label>
                        <div className="relative mt-1">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                            placeholder="max@beispiel.de"
                          />
                        </div>
                        {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                      </div>

                      <div>
                        <Label>Telefon (optional)</Label>
                        <div className="relative mt-1">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => handleChange('phone', e.target.value)}
                            className="pl-10"
                            placeholder="+49 123 456789"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <Label>Passwort *</Label>
                          <div className="relative mt-1">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              type="password"
                              value={formData.password}
                              onChange={(e) => handleChange('password', e.target.value)}
                              className={`pl-10 ${errors.password ? 'border-red-500' : ''}`}
                              placeholder="Min. 6 Zeichen"
                            />
                          </div>
                          {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
                        </div>
                        <div>
                          <Label>Passwort bestätigen *</Label>
                          <div className="relative mt-1">
                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <Input
                              type="password"
                              value={formData.password_confirm}
                              onChange={(e) => handleChange('password_confirm', e.target.value)}
                              className={`pl-10 ${errors.password_confirm ? 'border-red-500' : ''}`}
                              placeholder="Passwort wiederholen"
                            />
                          </div>
                          {errors.password_confirm && <p className="text-red-500 text-xs mt-1">{errors.password_confirm}</p>}
                        </div>
                      </div>

                      <Button type="button" className="w-full mt-6" onClick={handleNext}>
                        Weiter
                        <ArrowRight className="h-4 w-4 ml-2" />
                      </Button>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-4">
                      <div>
                        <Label>Shop-Name *</Label>
                        <div className="relative mt-1">
                          <Store className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            value={formData.shop_name}
                            onChange={(e) => handleChange('shop_name', e.target.value)}
                            className={`pl-10 ${errors.shop_name ? 'border-red-500' : ''}`}
                            placeholder="Mein Handmade Shop"
                          />
                        </div>
                        {errors.shop_name && <p className="text-red-500 text-xs mt-1">{errors.shop_name}</p>}
                        <p className="text-xs text-gray-500 mt-1">So wird Ihr Shop auf unserer Plattform angezeigt</p>
                      </div>

                      <div>
                        <Label>Shop-Beschreibung (optional)</Label>
                        <Textarea
                          value={formData.shop_description}
                          onChange={(e) => handleChange('shop_description', e.target.value)}
                          placeholder="Erzählen Sie uns etwas über Ihren Shop und Ihre Produkte..."
                          rows={3}
                          className="mt-1"
                        />
                      </div>

                      <div>
                        <Label>Steuernummer (optional)</Label>
                        <div className="relative mt-1">
                          <FileText className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                          <Input
                            value={formData.tax_id}
                            onChange={(e) => handleChange('tax_id', e.target.value)}
                            className="pl-10"
                            placeholder="DE123456789"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">Falls vorhanden - kann später ergänzt werden</p>
                      </div>

                      <div>
                        <Label>Geschäftsadresse (optional)</Label>
                        <div className="relative mt-1">
                          <Building className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                          <Textarea
                            value={formData.business_address}
                            onChange={(e) => handleChange('business_address', e.target.value)}
                            className="pl-10"
                            placeholder="Musterstraße 1&#10;12345 Musterstadt"
                            rows={2}
                          />
                        </div>
                      </div>

                      {errors.submit && (
                        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                          <AlertCircle className="h-4 w-4" />
                          {errors.submit}
                        </div>
                      )}

                      <div className="flex gap-3 mt-6">
                        <Button type="button" variant="outline" onClick={() => setStep(1)} className="flex-1">
                          Zurück
                        </Button>
                        <Button type="submit" disabled={loading} className="flex-1">
                          {loading ? 'Registrieren...' : 'Registrierung abschicken'}
                        </Button>
                      </div>
                    </div>
                  )}
                </form>

                <div className="mt-6 pt-6 border-t text-center text-sm text-gray-600">
                  Sie haben bereits einen Account?{' '}
                  <Link to="/login" className="text-purple-600 hover:underline font-medium">
                    Hier anmelden
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorRegister;
